import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useNoIndex } from '../../../common/hooks/useNoIndex.js';
import {
  obtenerHilo,
  obtenerRespuesta,
  pedirPreguntasDeInicio,
  preguntar,
  type AvisoDeProgreso,
  type FalloDeAthena,
} from '../../../common/services/api.js';
import { diccionarioDe } from '../../../i18n/index.js';
import type { Idioma } from '../../../i18n/index.js';
import { Caja, type ModoDeLaCaja } from '../../../common/components/Caja.js';
import { RegistroLimiteModal } from '../../cuentas/components/RegistroLimiteModal.js';
import { Esperando } from '../components/Esperando.js';
import { Renderer } from '../components/Renderer.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';

/**
 * La pantalla de consulta, la misma para los tres modos.
 *
 * Es una sola pantalla y no tres porque el motor es uno solo: tres pantallas separadas acabarían
 * divergiendo igual que lo harían tres pipelines (`plan/F3-contrastar-comparar.md`).
 *
 * La URL lleva el id de la consulta para poder volver a ella, pero **una respuesta solo la ve su
 * propia sesión**: el API responde 404 a cualquier otra. Compartir el enlace no publica nada.
 *
 * **El modo lo elige Athena** (MODO-01): desde la portada (`/consulta`) se manda `auto`, y encima
 * de la última respuesta se dice con qué modo se respondió, con botones para responder la misma
 * pregunta en los otros dos. `/preguntar`, `/contrastar` y `/comparar` siguen fijando su modo:
 * son los enlaces que ya circulan.
 *
 * **Se puede seguir preguntando** (CONV-01): cada respuesta queda en el hilo y la caja pasa debajo
 * de la última. El seguimiento manda el id de la última respuesta; el API sube a la raíz de la
 * conversación y solo si es de la misma sesión.
 */
export function ConsultaPage({ modo, idioma }: { readonly modo: ModoDeLaCaja; readonly idioma: Idioma }) {
  const { id } = useParams();
  const [parametros] = useSearchParams();
  const [enCurso, setEnCurso] = useState(false);
  const [fallo, setFallo] = useState<FalloDeAthena | null>(null);
  /** STREAM-01: el texto de la fase actual, tal como lo manda el motor por SSE. `null` hasta que llegue el primer aviso. */
  // Los avisos de progreso de la pregunta en curso, uno por fase alcanzada (el último es la actual).
  const [avisos, setAvisos] = useState<readonly AvisoDeProgreso[]>([]);
  const alProgresar = useCallback((aviso: AvisoDeProgreso) => {
    // Una fase puede avisar más de una vez (comprobar, reparar, comprobar): se actualiza su texto, no se repite.
    setAvisos(previos => (previos.at(-1)?.fase === aviso.fase ? [...previos.slice(0, -1), aviso] : [...previos, aviso]));
  }, []);
  /**
   * El hilo: cada pregunta con su respuesta. Una respuesta recuperada por URL llega sin su texto.
   */
  const [hilo, setHilo] = useState<
    readonly { readonly texto: string | null; readonly conversacionId: string | null; readonly respuesta: RespuestaDeAthena }[]
  >([]);
  const respuesta = hilo.at(-1)?.respuesta ?? null;
  const [noDisponible, setNoDisponible] = useState(false);
  /**
   * Lo que llega de la portada, en el estado de la navegación y **no en la URL**: un enlace con
   * `?enviar=` gastaría un modelo a cada persona que lo abriera. El estado no se comparte al copiar
   * el enlace.
   */
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const pathname = ubicacion.pathname;
  const desdeLaPortada = ubicacion.state as { texto?: string; enviar?: boolean } | null;
  const yaLimpiadoLaPortada = useRef(false);
  const t = diccionarioDe(idioma).consulta;

  // Las tres pantallas de consulta no se indexan: cada consulta cuesta dinero.
  useNoIndex(true);

  /** Envía y deja la respuesta al final del hilo. */
  const consultar = useCallback(
    (texto: string, modoPedido: ModoDeLaCaja, conversacionId: string | null, captchaToken: string | null) => {
      setEnCurso(true);
      setFallo(null);
      setAvisos([]);
      void preguntar({ texto, modo: modoPedido, idioma, conversacionId }, captchaToken, alProgresar)
        .then(r => {
          if (r.ok) {
            const turno = { texto, conversacionId, respuesta: r.respuesta };
            setHilo(h => [...h, turno]);
            // La URL pasa a llevar el id, para poder volver. No recarga: solo cambia la barra.
            const modoRespondido = r.respuesta.modo ?? (modoPedido === 'auto' ? 'preguntar' : modoPedido);
            window.history.replaceState(null, '', `/${modoRespondido}/${r.respuesta.preguntaId}`);
          } else {
            setFallo(r.fallo);
          }
        })
        .finally(() => {
          setEnCurso(false);
          setAvisos([]);
        });
    },
    [idioma],
  );

  const enviar = useCallback(
    (texto: string, captchaToken: string | null) => {
      consultar(texto, modo, respuesta?.preguntaId ?? null, captchaToken);
    },
    [consultar, modo, respuesta],
  );

  // Al volver a una URL con id (recargar, atrás, una prediseñada), se pide la respuesta guardada.
  useEffect(() => {
    if (id === undefined || respuesta !== null) return;
    let vigente = true;
    // Primero la conversación entera (CONV-01); si no hay, la respuesta suelta (una prediseñada).
    void obtenerHilo(id).then(async turnos => {
      if (!vigente) return;
      if (turnos.length > 0) {
        setHilo(turnos.map((t, i) => ({ texto: t.texto, conversacionId: turnos[i - 1]?.respuesta.preguntaId ?? null, respuesta: t.respuesta })));
        return;
      }
      const r = await obtenerRespuesta(id);
      if (!vigente) return;
      if (r === null) setNoDisponible(true);
      else setHilo([{ texto: null, conversacionId: null, respuesta: r }]);
    });
    return () => {
      vigente = false;
    };
  }, [id, respuesta]);

  /**
   * Una prediseñada llega sin su pregunta: es de otra sesión y el hilo sale vacío. Se busca entre
   * las de la portada por su respuesta, y se escribe en el idioma de la interfaz aunque la respuesta
   * sea la del otro idioma (se cruza por la sugerencia). Vale también al recargar o al abrir el enlace.
   */
  const [textoPrediseñada, setTextoPrediseñada] = useState<string | null>(null);
  const soloUnaSinTexto = hilo.length === 1 && hilo[0]?.texto === null;
  useEffect(() => {
    if (id === undefined || !soloUnaSinTexto) return;
    let vigente = true;
    const otro: Idioma = idioma === 'es' ? 'en' : 'es';
    void Promise.all([pedirPreguntasDeInicio(idioma), pedirPreguntasDeInicio(otro)]).then(([aqui, alli]) => {
      if (!vigente) return;
      const sugerencia = aqui.find(p => p.respuestaId === id) ?? aqui.find(p => p.id === alli.find(q => q.respuestaId === id)?.id);
      setTextoPrediseñada(sugerencia?.texto ?? null);
    });
    return () => {
      vigente = false;
    };
  }, [id, idioma, soloUnaSinTexto]);

  // Una pregunta escrita en la portada se borra del estado de navegación una sola vez al llegar,
  // para que recargar no vuelva a intentar el envío automático (y a gastar) la misma pregunta. El
  // envío en sí lo dispara la propia `Caja` de abajo (`enviarAlMontar`), que espera a tener el
  // captcha listo antes de mandar nada: no se manda aquí sin pasar por esa espera.
  useEffect(() => {
    if (yaLimpiadoLaPortada.current || desdeLaPortada?.enviar !== true || desdeLaPortada.texto === undefined) return;
    yaLimpiadoLaPortada.current = true;
    void navegar(pathname, { replace: true, state: { texto: desdeLaPortada.texto } });
  }, [desdeLaPortada, navegar, pathname]);

  return (
    <div className='flex grow flex-col'>
      <RegistroLimiteModal idioma={idioma} fallo={fallo} />
      <main className='mx-auto flex w-full max-w-[780px] min-[1100px]:max-w-[1200px] grow flex-col gap-8 px-6 py-10'>
        {hilo.length === 0 ? (
          <div className='w-full max-w-[732px]'>
            <Caja
              idioma={idioma}
              modo={modo}
              enCurso={enCurso}
              fallo={fallo}
              textoInicial={desdeLaPortada?.texto ?? parametros.get('q') ?? ''}
              enviarAlMontar={desdeLaPortada?.enviar === true}
              onEnviar={enviar}
            />
          </div>
        ) : null}
        {hilo
          .map((turno, i) => ({ ...turno, texto: turno.texto ?? (i === 0 ? textoPrediseñada : null) }))
          .map((turno, i) => (
            <section key={turno.respuesta.preguntaId} aria-label={turno.texto ?? undefined} className='flex flex-col gap-4'>
              {/* La pregunta es el título de la página (Iván, 2026-10-02): en la línea del h1 de la web y
                  claramente por encima de los ## y ### que traiga la respuesta. */}
              {turno.texto === null ? null : i === 0 ? (
                <h1 className='m-0 mb-2 max-w-[760px] font-(family-name:--serif) text-[28px] leading-[1.18] font-bold tracking-[-0.01em] text-balance text-(--ink) min-[700px]:text-[34px]'>
                  {conMayuscula(turno.texto)}
                </h1>
              ) : (
                // Los seguimientos, un escalón por debajo de la primera pregunta y con una raya que los separa.
                <h2 className='m-0 mt-6 mb-2 border-t border-(--line) pt-10 font-(family-name:--serif) text-[24px] leading-[1.2] font-bold text-balance text-(--ink) min-[700px]:text-[28px]'>
                  {conMayuscula(turno.texto)}
                </h2>
              )}
              <Renderer respuesta={turno.respuesta} idioma={idioma} />
            </section>
          ))}
        {/* Mientras se espera, en qué anda. Veinte segundos en blanco se leen como una pantalla colgada. */}
        {enCurso ? <Esperando idioma={idioma} avisos={avisos} /> : null}
        {hilo.length === 0 ? null : (
          <div className='w-full max-w-[732px]'>
            <Caja key={hilo.length} idioma={idioma} modo={modo} enCurso={enCurso} fallo={fallo} textoInicial='' onEnviar={enviar} />
          </div>
        )}
        {respuesta === null && id !== undefined && !enCurso ? (
          <p role='status' className='font-(family-name:--text) text-[15px] text-(--ink-soft)'>
            {noDisponible ? t.noDisponible : t.recuperando}
          </p>
        ) : null}
      </main>
    </div>
  );
}

/** La primera letra en mayúscula, solo al pintar: la pregunta se guarda tal como la escribió. */
function conMayuscula(texto: string): string {
  const i = texto.search(/\p{L}/u);
  return i === -1 ? texto : texto.slice(0, i) + texto.charAt(i).toLocaleUpperCase() + texto.slice(i + 1);
}
